// src/ui/screens/post/AdDetailsScreen.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../../../firebase';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  cardLight: '#2C2F36',
};

const AD_ASPECT_RATIO = 179.4074 / 443.8519;

// ── Safely normalize any of the shapes `createdAt` can arrive in ───────────
// Firestore Timestamps have a `.toDate()` method, but once an ad object is
// passed through react-router's navigation `state`, it gets serialized —
// the Timestamp class instance is stripped down to a plain
// `{ seconds, nanoseconds }` object, losing `.toDate()`. This helper covers
// every shape we might realistically receive: a live Timestamp, a
// serialized Timestamp-like object, an ISO string, a plain number
// (epoch ms), or an already-constructed Date.
function toJsDate(value) {
  if (!value) return null;

  // Already a Date
  if (value instanceof Date) return value;

  // Live Firestore Timestamp instance
  if (typeof value.toDate === 'function') {
    try {
      return value.toDate();
    } catch {
      return null;
    }
  }

  // Serialized Timestamp-like object: { seconds, nanoseconds }
  if (typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1e6));
  }

  // ISO string or epoch number
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
}

export const AdDetailsScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const adData = location.state?.adData || {};

  const [imageError, setImageError] = useState(false);

  const title = adData.title || 'Ad Details';
  const description = adData.description || '';
  const imageUrl = adData.imageUrl || '';
  const linkUrl = adData.linkUrl || '';
  const createdAt = toJsDate(adData.createdAt);
  // Note: viewCount/clickCount are intentionally not read here — they're
  // still incremented below on every view/click (see useEffect and
  // handleLaunchLink) so the data lives in Firestore for backend/admin
  // reporting, but they're no longer surfaced in this client-facing UI.

  // ── Format date ───────────────────────────────────────────────────────────

  const formattedDate = createdAt
    ? `${createdAt.getDate()}/${createdAt.getMonth() + 1}/${createdAt.getFullYear()}`
    : 'Unknown date';

  // ── Increment view count on mount ────────────────────────────────────────

  useEffect(() => {
    if (adData.id) {
      updateDoc(doc(db, 'ads', adData.id), {
        viewCount: increment(1),
      }).catch((error) => console.error('Error updating view count:', error));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adData.id]);

  // ── Launch link ───────────────────────────────────────────────────────────

  const handleLaunchLink = async () => {
    if (!linkUrl) return;

    // Increment click count
    if (adData.id) {
      try {
        await updateDoc(doc(db, 'ads', adData.id), {
          clickCount: increment(1),
        });
      } catch (error) {
        console.error('Error updating click count:', error);
      }
    }

    let url = linkUrl;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = `https://${url}`;
    }

    // Open in new tab/window
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // ── Go back ───────────────────────────────────────────────────────────────

  const handleBack = () => {
    navigate(-1);
  };

  // ── Calculate ad height ─────────────────────────────────────────────────
  // Height is derived from the viewport width but capped at 300px, so on a
  // wide desktop window this still resolves to the 300px cap rather than
  // stretching — no separate desktop-specific calculation is needed here.

  const screenWidth = window.innerWidth;
  const adHeight = Math.min(screenWidth * AD_ASPECT_RATIO, 300);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-filatom-bg">
      {/* App Bar
          Desktop: contents center to the same column as the page body
          below and gain a hairline bottom border, instead of stretching
          full-width. */}
      <div className="sticky top-0 z-10 bg-filatom-bg md:border-b md:border-white/5">
        <div className="flex items-center justify-between px-4 h-14 md:h-16 md:max-w-3xl md:mx-auto md:px-10">
          <button onClick={handleBack} className="text-white p-2 -ml-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-white text-lg font-semibold">Ad Details</span>
          <div className="w-9" />
        </div>
      </div>

      {/* Content
          Desktop: contained to a centered, readable column (same width as
          the Edit Profile / Add Property pages) with the hero image
          rounded on all corners instead of flush to the screen edges. All
          of this rides on `md:` variants layered onto the original mobile
          classes, so nothing below md:768px changes from the original
          markup. */}
      <div className="pb-8 md:max-w-3xl md:mx-auto md:px-10 md:pt-8 md:pb-16">
        {/* Ad Image */}
        <div
          className="w-full rounded-b-xl overflow-hidden md:rounded-xl"
          style={{
            height: adHeight,
            backgroundColor: COLORS.card,
          }}
        >
          {imageUrl && !imageError ? (
            <img
              src={imageUrl}
              alt={title}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: COLORS.cardLight }}>
              <svg className="w-12 h-12 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
        </div>

        {/* Title Section */}
        <div className="px-5 md:px-0 mt-5 md:mt-8">
          <h1 className="text-white text-2xl md:text-3xl font-bold leading-tight">{title}</h1>
          <div className="flex items-center gap-3 mt-2">
            <span
              className="px-2 py-0.5 rounded text-[11px] font-semibold"
              style={{
                backgroundColor: `${COLORS.orange}1F`,
                color: COLORS.orange,
              }}
            >
              Sponsored
            </span>
            <span className="text-white/40 text-xs">Posted on {formattedDate}</span>
          </div>
        </div>

        {/* Description Section */}
        {description && (
          <div className="px-5 md:px-0 mt-6 md:mt-8">
            <h2 className="text-white text-lg font-semibold">About this ad</h2>
            <div
              className="mt-2.5 p-4 md:p-5 rounded-xl"
              style={{ backgroundColor: COLORS.card }}
            >
              <p className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap">
                {description}
              </p>
            </div>
          </div>
        )}

        {/* Link Section */}
        <div className="px-5 md:px-0 mt-6 md:mt-8">
          <h2 className="text-white text-lg font-semibold">Learn more</h2>
          <button
            onClick={handleLaunchLink}
            className="w-full mt-2.5 p-4 md:p-5 rounded-xl flex items-center gap-3.5 border"
            style={{
              backgroundColor: COLORS.card,
              borderColor: `${COLORS.orange}4D`,
            }}
          >
            <div
              className="p-2.5 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${COLORS.orange}1F` }}
            >
              <svg className="w-5 h-5" fill="none" stroke={COLORS.orange} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <div className="flex-1 text-left min-w-0">
              <span className="text-white/50 text-xs">Tap to open link</span>
              <p className="text-filatom-primary text-sm font-medium underline underline-offset-2 truncate">
                {linkUrl || 'No link provided'}
              </p>
            </div>
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${COLORS.orange}1F` }}
            >
              <svg className="w-4 h-4" fill="none" stroke={COLORS.orange} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdDetailsScreen;